import { ChartOptions } from 'chart.js'
import moment from 'moment'
import React, {
  FunctionComponent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Line } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'

import { BatchLoader } from '../../../App/Helpers/batchIdHandler/BatchLoader'
import { IOccupancyRateReportDashboard } from '../../OccupancyRateReport/Client/IOccupancyRateReport'
import { DashBoardCardTitle, DashboardCardTopLeftContainer } from '../styles'

import { Client } from './Client/Client'
import { IProps } from './IProps'
import {
  FailedAttemptMessage,
  LineContainer,
  NoOccupancyLabel,
  OccupancyRateCardContainer,
  PercentageContainer,
  PercentageLabel,
} from './style'

export const OccupancyRateCard: FunctionComponent<IProps> = ({
  from,
  to,
  windowWidth,
}) => {
  const { t } = useTranslation()
  const chartRef = useRef(null)
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [],
  })

  const [status, setStatus] = useState<string>('Processing')
  const [processed, setProcessed] = useState<number>(0)
  const [total, setTotal] = useState<number>(0)

  const [attempt, setAttempt] = useState<number>(1)

  const [showLoader, setShowLoader] = useState(false)

  const [reportsArray, setReportsArray] = useState<
    IOccupancyRateReportDashboard[]
  >([])

  const [failedAttemptMessage, setFailedAttemptMessage] = useState<string>('')

  const [generatedPercentage, setGeneratedPercentage] = useState<string>('')
  const [reportValues, reportLabels]: [number[], string[]] = useMemo(() => {
    const values = Array.from({ length: from.daysInMonth() }, () => 0)
    const labels = Array.from({ length: from.daysInMonth() }, (_, i) =>
      from.clone().add(i, 'days').format('DD/MM'),
    )

    for (const rateReport of reportsArray) {
      const date = moment(rateReport.date)
      if (rateReport.workingMinutes === 0) {
        continue
      }
      const datePosition = date.diff(from, 'days', false)
      labels[datePosition] = date.format('DD/MM')
      values[datePosition] = Math.round(
        (rateReport.appointmentMinutes / rateReport.workingMinutes) * 100,
      )
    }

    const totalSum = values.reduce((partialSum, a) => partialSum + a, 0)
    const countDaysWithValues = values.filter(v => v !== null && v !== 0).length

    if (countDaysWithValues > 0) {
      setGeneratedPercentage(
        Math.trunc(totalSum / countDaysWithValues)
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, ','),
      )
    }

    return [values, labels]
  }, [reportsArray, from])

  useEffect(() => {
    const chart = chartRef.current
    if (chart) {
      const gradient = chart.ctx.createLinearGradient(0, 300, 0, 0)
      gradient.addColorStop(0, 'rgba(255, 255, 255,0.1)')
      gradient.addColorStop(1, 'rgba(255, 255, 255,0.9)')
      setChartData({
        labels: reportLabels,
        datasets: [
          {
            data: reportValues,
            fill: true,
            backgroundColor: gradient,
            borderColor: 'rgba(255, 255, 255,1)',
            yAxisID: 'yAxis',
            xAxisID: 'xAxis',
          },
        ],
      })
    }
  }, [reportValues])

  useEffect(() => {
    const callback = async () => {
      const response = await Client.getOccupancyReportForDashboard({
        from,
        to,
        attempt,
        batchSetter: {
          setStatus,
          setProcessed,
          setTotal,
          setShowLoader,
          setFailedAttemptMessage,
        },
      })

      if (Array.isArray(response)) {
        setReportsArray(response)
      } else {
        setReportsArray([])
      }

      setShowLoader(false)
    }

    callback().then(r => console.log('callback'))
  }, [from, to, attempt])

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      xAxis: {
        grid: {
          color: 'rgba(0, 0, 0, 0)',
        },
        ticks: {
          display: false,
        },
      },
      yAxis: {
        grid: {
          color: 'rgba(0, 0, 0, 0)',
        },
        ticks: {
          display: false,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        callbacks: {
          afterLabel() {
            return ' %'
          },
        },
      },
    },
  }

  const hasNoData = reportValues.some(value => {
    return value !== 0
  })

  return (
    <DashboardCardTopLeftContainer windowWidth={windowWidth}>
      <DashBoardCardTitle>
        {t('Occupancy rate').toUpperCase()}
      </DashBoardCardTitle>

      <OccupancyRateCardContainer>
        {showLoader ? (
          <BatchLoader status={status} processed={processed} total={total} />
        ) : hasNoData ? (
          <>
            <PercentageContainer width={windowWidth}>
              <PercentageLabel width={windowWidth}>
                {generatedPercentage}%
              </PercentageLabel>
            </PercentageContainer>
            <LineContainer width={windowWidth}>
              <Line ref={chartRef} data={chartData} options={options} />
            </LineContainer>
          </>
        ) : failedAttemptMessage ? (
          <FailedAttemptMessage>
            {' '}
            🚧 {t(failedAttemptMessage)}
          </FailedAttemptMessage>
        ) : (
          <NoOccupancyLabel>
            {t('No occupancy rate for this period')} 📈
          </NoOccupancyLabel>
        )}
      </OccupancyRateCardContainer>
    </DashboardCardTopLeftContainer>
  )
}
